import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api, { timeSlots, bookings, users, serviceTypes, onlineClasses } from '../services/api';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { DashboardLayout } from '../components/Layout';
import './AdminDashboard.css';

export function AdminDashboard() {
  const location = useLocation();
  const navigate = useNavigate();

  // Get active section from URL
  const searchParams = new URLSearchParams(location.search);
  // Secções que continuam a viver aqui (as únicas que nenhum ecrã novo faz): horários, reservas, aulas
  // online, tipos de aulas e anúncios. As outras saíram — «dashboard» foi para /admin, os planos de treino
  // para o estúdio (determinístico, sem o gerador «por IA») e as definições para /admin/settings.
  const secoesAntigas = ['timeslots', 'bookings', 'classes', 'services', 'notifications'];
  const pedida = searchParams.get('section');
  const activeSection = secoesAntigas.includes(pedida) ? pedida : null;

  // Pedido de uma secção que já não existe aqui (ou nenhuma): segue para o painel novo.
  useEffect(() => {
    if (!activeSection) navigate('/admin', { replace: true });
  }, [activeSection, navigate]);


  return (
    <DashboardLayout userType="admin">
      {/* Dashboard Overview */}
      {activeSection === 'timeslots' && <TimeSlotsTab />}
      {activeSection === 'bookings' && <BookingsTab />}
      {activeSection === 'classes' && <OnlineClassesTab />}
      {activeSection === 'services' && <ServiceTypesTab />}
      {activeSection === 'notifications' && <NotificationsTab />}
    </DashboardLayout>
  );
}

// ============================================
// TimeSlotsTab Component
// ============================================
function TimeSlotsTab() {
  const [slotList, setSlotList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  // Filters
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [formData, setFormData] = useState({
    date: '',
    startTime: '',
    endTime: '',
    maxCapacity: 4
  });
  
  const [bulkData, setBulkData] = useState({
    startDate: '',
    endDate: '',
    maxCapacity: 4,
    schedules: [
      { id: 1, dayOfWeek: 1, startTime: '08:00', endTime: '12:00', enabled: true },
      { id: 2, dayOfWeek: 1, startTime: '14:00', endTime: '21:00', enabled: true },
    ]
  });

  const daysOfWeek = [
    { value: 1, label: 'Segunda' },
    { value: 2, label: 'Terça' },
    { value: 3, label: 'Quarta' },
    { value: 4, label: 'Quinta' },
    { value: 5, label: 'Sexta' },
    { value: 6, label: 'Sábado' },
    { value: 0, label: 'Domingo' }
  ];

  useEffect(() => {
    loadTimeSlots();
  }, []);

  const loadTimeSlots = async () => {
    try {
      // Admin vê todos os horários, incluindo bloqueados e não-released
      const response = await timeSlots.getAll({ includeBlocked: true, includeAll: true });
      setSlotList(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar horários');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSlot) {
        await timeSlots.update(editingSlot.id, formData);
        toast.success('Horário atualizado com sucesso');
      } else {
        await timeSlots.create(formData);
        toast.success('Horário criado com sucesso');
      }
      setShowModal(false);
      setEditingSlot(null);
      resetForm();
      loadTimeSlots();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar horário');
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    
    if (!bulkData.startDate || !bulkData.endDate) {
      toast.error('Selecione o intervalo de datas');
      return;
    }

    const enabledSchedules = bulkData.schedules.filter(s => s.enabled);
    if (enabledSchedules.length === 0) {
      toast.error('Adicione pelo menos um horário');
      return;
    }

    try {
      const start = new Date(bulkData.startDate);
      const end = new Date(bulkData.endDate);
      const slotsToCreate = [];

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dayOfWeek = d.getDay();
        const daySchedules = enabledSchedules.filter(s => s.dayOfWeek === dayOfWeek);
        
        for (const schedule of daySchedules) {
          slotsToCreate.push({
            date: d.toISOString().split('T')[0],
            startTime: schedule.startTime,
            endTime: schedule.endTime,
            maxCapacity: bulkData.maxCapacity
          });
        }
      }

      if (slotsToCreate.length === 0) {
        toast.error('Nenhum horário para criar no intervalo selecionado');
        return;
      }

      await timeSlots.createBulk(slotsToCreate);
      toast.success(`${slotsToCreate.length} horários criados com sucesso!`);
      setShowBulkModal(false);
      loadTimeSlots();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao criar horários');
    }
  };

  const addSchedule = () => {
    const newId = Math.max(...bulkData.schedules.map(s => s.id), 0) + 1;
    setBulkData({
      ...bulkData,
      schedules: [
        ...bulkData.schedules,
        { id: newId, dayOfWeek: 1, startTime: '09:00', endTime: '18:00', enabled: true }
      ]
    });
  };

  const removeSchedule = (id) => {
    setBulkData({
      ...bulkData,
      schedules: bulkData.schedules.filter(s => s.id !== id)
    });
  };

  const updateSchedule = (id, field, value) => {
    setBulkData({
      ...bulkData,
      schedules: bulkData.schedules.map(s => 
        s.id === id ? { ...s, [field]: value } : s
      )
    });
  };

  const resetForm = () => {
    setFormData({
      date: '',
      startTime: '',
      endTime: '',
      maxCapacity: 4
    });
  };

  const handleEdit = (slot) => {
    const startDate = new Date(slot.startTime);
    const endDate = new Date(slot.endTime);
    setEditingSlot(slot);
    setFormData({
      date: format(startDate, 'yyyy-MM-dd'),
      startTime: format(startDate, 'HH:mm'),
      endTime: format(endDate, 'HH:mm'),
      maxCapacity: slot.maxCapacity
    });
    setShowModal(true);
  };

  const handleBlock = async (id) => {
    try {
      await timeSlots.block(id);
      toast.success('Horário bloqueado');
      loadTimeSlots();
    } catch (error) {
      toast.error('Erro ao bloquear horário');
    }
  };

  const handleUnblock = async (id) => {
    try {
      await timeSlots.unblock(id);
      toast.success('Horário desbloqueado');
      loadTimeSlots();
    } catch (error) {
      toast.error('Erro ao desbloquear horário');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja eliminar este horário?')) {
      try {
        await timeSlots.delete(id);
        toast.success('Horário eliminado');
        loadTimeSlots();
      } catch (error) {
        toast.error('Erro ao eliminar horário');
      }
    }
  };

  const getStatusBadge = (slot) => {
    if (slot.isBlocked) return <span className="status-badge cancelled">Bloqueado</span>;
    if (slot.isFull) return <span className="status-badge pending">Cheio</span>;
    return <span className="status-badge confirmed">Disponível</span>;
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner"></div></div>;
  }

  return (
    <div className="data-card">
      <div className="card-header">
        <h3 className="card-title">Gestão de Horários</h3>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-outline" onClick={() => setShowModal(true)}>
            + Individual
          </button>
          <button className="btn btn-primary" onClick={() => setShowBulkModal(true)}>
            📅 Em Massa
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', padding: '16px 0', borderBottom: '1px solid var(--border-color, #e2e8f0)', marginBottom: '16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--info)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📅 Data De</label>
          <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--info)', fontSize: '14px' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--info)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📅 Data Até</label>
          <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--info)', fontSize: '14px' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--info)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📊 Estado</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--info)', fontSize: '14px', minWidth: '140px' }}>
            <option value="">Todos</option>
            <option value="available">Disponível</option>
            <option value="blocked">Bloqueado</option>
            <option value="full">Cheio</option>
          </select>
        </div>
        {(filterDateFrom || filterDateTo || filterStatus) && (
          <button onClick={() => { setFilterDateFrom(''); setFilterDateTo(''); setFilterStatus(''); }} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--info)', background: 'var(--info-bg)', color: 'var(--info)', fontSize: '13px', cursor: 'pointer', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px', height: '37px' }}>
            ✕ Limpar filtros
          </button>
        )}
      </div>

      <table className="modern-table adm-table">
        <thead>
          <tr>
            <th>Data</th>
            <th>Início</th>
            <th>Fim</th>
            <th>Capacidade</th>
            <th>Reservas</th>
            <th>Estado</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {(() => {
            const filteredSlots = slotList.filter((slot) => {
              const startDate = slot.startTime ? new Date(slot.startTime) : null;
              if (filterDateFrom && startDate) {
                const from = new Date(filterDateFrom);
                if (startDate < from) return false;
              }
              if (filterDateTo && startDate) {
                const to = new Date(filterDateTo);
                to.setHours(23, 59, 59, 999);
                if (startDate > to) return false;
              }
              if (filterStatus === 'blocked' && !slot.isBlocked) return false;
              if (filterStatus === 'available' && (slot.isBlocked || slot.isFull)) return false;
              if (filterStatus === 'full' && !slot.isFull) return false;
              return true;
            });
            return filteredSlots.length > 0 ? filteredSlots.map((slot) => {
            const startDate = slot.startTime ? new Date(slot.startTime) : null;
            const endDate = slot.endTime ? new Date(slot.endTime) : null;
            return (
              <tr key={slot.id}>
                <td data-label="Data">{startDate && !isNaN(startDate) ? format(startDate, 'dd/MM/yyyy') : '-'}</td>
                <td data-label="Início">{startDate && !isNaN(startDate) ? format(startDate, 'HH:mm') : '-'}</td>
                <td data-label="Fim">{endDate && !isNaN(endDate) ? format(endDate, 'HH:mm') : '-'}</td>
                <td data-label="Capacidade">{slot.maxCapacity}</td>
                <td data-label="Reservas">{slot.currentBookings || 0}</td>
                <td data-label="Estado">{getStatusBadge(slot)}</td>
                <td className="adm-actions">
                  <div className="action-buttons">
                    <button className="btn-icon" onClick={() => handleEdit(slot)} title="Editar">✏️</button>
                    {slot.isBlocked ? (
                      <button className="btn-icon" onClick={() => handleUnblock(slot.id)} title="Desbloquear">🔓</button>
                    ) : (
                      <button className="btn-icon" onClick={() => handleBlock(slot.id)} title="Bloquear">🔒</button>
                    )}
                    <button className="btn-icon danger" onClick={() => handleDelete(slot.id)} title="Eliminar">🗑️</button>
                  </div>
                </td>
              </tr>
            );
          }) : (
            <tr>
              <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                {slotList.length === 0 ? 'Nenhum horário criado. Clique em "Em Massa" para criar horários.' : 'Nenhum horário corresponde aos filtros selecionados.'}
              </td>
            </tr>
          );
          })()}
        </tbody>
      </table>

      {/* Modal Individual */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-modern" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingSlot ? 'Editar Horário' : 'Novo Horário'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Data</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Hora Início</label>
                    <input
                      type="time"
                      className="form-input"
                      value={formData.startTime}
                      onChange={(e) => setFormData({...formData, startTime: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Hora Fim</label>
                    <input
                      type="time"
                      className="form-input"
                      value={formData.endTime}
                      onChange={(e) => setFormData({...formData, endTime: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Capacidade</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.maxCapacity}
                    onChange={(e) => setFormData({...formData, maxCapacity: parseInt(e.target.value)})}
                    min="1"
                    required
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Bulk */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal-modern modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Criar Horários em Massa</h3>
              <button className="modal-close" onClick={() => setShowBulkModal(false)}>×</button>
            </div>
            <form onSubmit={handleBulkSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Data Início</label>
                    <input
                      type="date"
                      className="form-input"
                      value={bulkData.startDate}
                      onChange={(e) => setBulkData({...bulkData, startDate: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Data Fim</label>
                    <input
                      type="date"
                      className="form-input"
                      value={bulkData.endDate}
                      onChange={(e) => setBulkData({...bulkData, endDate: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Capacidade</label>
                    <input
                      type="number"
                      className="form-input"
                      value={bulkData.maxCapacity}
                      onChange={(e) => setBulkData({...bulkData, maxCapacity: parseInt(e.target.value)})}
                      min="1"
                      required
                    />
                  </div>
                </div>

                <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Horários por Dia da Semana</h4>
                
                {bulkData.schedules.map((schedule) => (
                  <div key={schedule.id} className="schedule-row">
                    <input
                      type="checkbox"
                      checked={schedule.enabled}
                      onChange={(e) => updateSchedule(schedule.id, 'enabled', e.target.checked)}
                    />
                    <select
                      className="form-input"
                      value={schedule.dayOfWeek}
                      onChange={(e) => updateSchedule(schedule.id, 'dayOfWeek', parseInt(e.target.value))}
                    >
                      {daysOfWeek.map(day => (
                        <option key={day.value} value={day.value}>{day.label}</option>
                      ))}
                    </select>
                    <input
                      type="time"
                      className="form-input"
                      value={schedule.startTime}
                      onChange={(e) => updateSchedule(schedule.id, 'startTime', e.target.value)}
                    />
                    <span>até</span>
                    <input
                      type="time"
                      className="form-input"
                      value={schedule.endTime}
                      onChange={(e) => updateSchedule(schedule.id, 'endTime', e.target.value)}
                    />
                    <button type="button" className="btn-icon danger" onClick={() => removeSchedule(schedule.id)}>🗑️</button>
                  </div>
                ))}

                <button type="button" className="btn btn-outline" onClick={addSchedule} style={{ marginTop: '10px' }}>
                  + Adicionar Horário
                </button>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowBulkModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Criar Horários</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// ============================================
function OnlineClassesTab() {
  const [classes, setClasses] = useState([]);
  const [userList, setUserList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [assignToAll, setAssignToAll] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  // Filters
  const [filterClassSearch, setFilterClassSearch] = useState('');
  const [filterClassType, setFilterClassType] = useState('');
  const [filterClassDifficulty, setFilterClassDifficulty] = useState('');
  const [filterClassCategory, setFilterClassCategory] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'Recorded',
    category: '',
    difficulty: 'Iniciante',
    durationMinutes: 30,
    videoUrl: '',
    thumbnailUrl: '',
    instructorName: '',
    isFreePreview: false,
    creditsRequired: 1
  });

  useEffect(() => {
    loadClasses();
    loadUsers();
  }, []);

  const loadClasses = async () => {
    try {
      const response = await onlineClasses.getAll();
      setClasses(Array.isArray(response.data) ? response.data : response.data?.items || []);
    } catch (error) {
      console.error('Erro ao carregar aulas:', error);
      setClasses([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await users.getAll();
      setUserList(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar utilizadores (Reports section):', error);
      toast.error('Erro ao carregar utilizadores');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Título é obrigatório');
      return;
    }

    try {
      const dataToSend = {
        title: formData.title,
        description: formData.description,
        type: formData.type,
        category: formData.category,
        difficulty: formData.difficulty,
        durationMinutes: parseInt(formData.durationMinutes) || 30,
        videoUrl: formData.videoUrl,
        thumbnailUrl: formData.thumbnailUrl,
        instructorName: formData.instructorName,
        isFreePreview: formData.isFreePreview,
        creditsRequired: parseInt(formData.creditsRequired) || 0
      };

      if (editingClass) {
        await onlineClasses.update(editingClass.id, dataToSend);
        toast.success('Aula atualizada com sucesso!');
      } else {
        await onlineClasses.create(dataToSend);
        toast.success('Aula criada com sucesso!');
      }

      setShowForm(false);
      setEditingClass(null);
      resetForm();
      loadClasses();
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao guardar aula');
    }
  };

  const handleEdit = (classItem) => {
    setEditingClass(classItem);
    setFormData({
      title: classItem.title || '',
      description: classItem.description || '',
      type: classItem.type || 'Recorded',
      category: classItem.category || '',
      difficulty: classItem.difficulty || 'Iniciante',
      durationMinutes: classItem.durationMinutes || 30,
      videoUrl: classItem.videoUrl || '',
      thumbnailUrl: classItem.thumbnailUrl || '',
      instructorName: classItem.instructorName || '',
      isFreePreview: classItem.isFreePreview || false,
      creditsRequired: classItem.creditsRequired || 1
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem a certeza que deseja eliminar esta aula?')) return;
    try {
      await onlineClasses.delete(id);
      toast.success('Aula eliminada');
      loadClasses();
    } catch (error) {
      toast.error('Erro ao eliminar aula');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      type: 'Recorded',
      category: '',
      difficulty: 'Iniciante',
      durationMinutes: 30,
      videoUrl: '',
      thumbnailUrl: '',
      instructorName: '',
      isFreePreview: false,
      creditsRequired: 1
    });
    setSelectedUsers([]);
    setAssignToAll(false);
    setUserSearchTerm('');
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === userList.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(userList.map(u => u.id));
    }
  };

  const filteredUsers = userList.filter(u => 
    u.name?.toLowerCase().includes(userSearchTerm.toLowerCase()) || 
    u.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  const getTypeBadge = (type) => {
    const types = {
      'Live': { label: '🔴 Ao Vivo', color: 'var(--error)' },
      'Recorded': { label: '📹 Gravada', color: 'var(--accent-primary)' },
      'Hybrid': { label: '🔀 Híbrida', color: 'var(--accent-primary)' }
    };
    const t = types[type] || types['Recorded'];
    return <span style={{ background: t.color, color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '12px' }}>{t.label}</span>;
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner"></div></div>;
  }

  return (
    <div className="data-card">
      <div className="card-header">
        <h3 className="card-title">🎬 Aulas Online</h3>
        <button className="btn btn-primary" onClick={() => { resetForm(); setEditingClass(null); setShowForm(true); }}>
          + Nova Aula
        </button>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content clean-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '920px', width: '95%', maxHeight: '90vh', overflow: 'auto', background: 'white', borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '32px 40px 24px', borderBottom: '1px solid var(--info)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '26px', fontWeight: '600', color: 'var(--info-text)' }}>{editingClass ? 'Editar Aula Online' : 'Nova Aula Online'}</h3>
                  <p style={{ margin: '8px 0 0 0', fontSize: '15px', color: 'var(--info)' }}>Configure os detalhes da aula e gerencie o acesso</p>
                </div>
                <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', color: 'var(--info)', fontSize: '28px', cursor: 'pointer', padding: '0', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', transition: 'all 0.2s' }} onMouseOver={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#0f172a'; }} onMouseOut={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#94a3b8'; }}>×</button>
              </div>
            </div>
            <form onSubmit={handleSubmit} style={{ padding: '32px 40px' }}>
              
              {/* Informações Básicas */}
              <div style={{ marginBottom: '32px' }}>
                <h4 style={{ margin: '0 0 20px 0', fontSize: '15px', color: 'var(--info-text)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Informações Básicas</h4>
                
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '10px', display: 'block', color: 'var(--info-text)' }}>Título da Aula *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Ex: Yoga para Iniciantes"
                    required
                    style={{ width: '100%', padding: '13px 16px', fontSize: '15px', borderRadius: '10px', border: '1px solid var(--info)', outline: 'none', transition: 'border-color 0.2s' }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div className="form-group">
                    <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block' }}>Tipo</label>
                    <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} style={{ width: '100%', padding: '14px', fontSize: '16px', borderRadius: '8px', border: '2px solid var(--border-color)' }}>
                      <option value="Recorded">📹 Gravada</option>
                      <option value="Live">🔴 Ao Vivo</option>
                      <option value="Hybrid">🔀 Híbrida</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block' }}>Dificuldade</label>
                    <select value={formData.difficulty} onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })} style={{ width: '100%', padding: '14px', fontSize: '16px', borderRadius: '8px', border: '2px solid var(--border-color)' }}>
                      <option value="Iniciante">🟢 Iniciante</option>
                      <option value="Intermédio">🟡 Intermédio</option>
                      <option value="Avançado">🔴 Avançado</option>
                    </select>
                  </div>
                </div>

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block' }}>Categoria</label>
                    <input
                      type="text"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      placeholder="Ex: Yoga, HIIT, Musculação"
                      style={{ width: '100%', padding: '14px', fontSize: '16px', borderRadius: '8px', border: '2px solid var(--border-color)' }}
                    />
                  </div>
                  <div className="form-group">
                    <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block' }}>Duração (min)</label>
                    <input
                      type="number"
                      value={formData.durationMinutes}
                      onChange={(e) => setFormData({ ...formData, durationMinutes: e.target.value })}
                      min="5"
                      max="180"
                      style={{ width: '100%', padding: '14px', fontSize: '16px', borderRadius: '8px', border: '2px solid var(--border-color)' }}
                    />
                  </div>
                </div>
              </div>

              {/* Descrição e Mídia */}
              <div style={{ background: 'var(--info-bg)', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: 'var(--text-primary)', fontWeight: '600' }}>🎨 Descrição e Mídia</h4>
                
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block' }}>Descrição</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descreva a aula..."
                    rows="3"
                    style={{ width: '100%', padding: '14px', fontSize: '16px', borderRadius: '8px', border: '2px solid var(--border-color)', resize: 'vertical' }}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block' }}>URL do Vídeo</label>
                  <input
                    type="url"
                    value={formData.videoUrl}
                    onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                    placeholder="https://youtube.com/... ou https://vimeo.com/..."
                    style={{ width: '100%', padding: '14px', fontSize: '16px', borderRadius: '8px', border: '2px solid var(--border-color)' }}
                  />
                </div>

                <div className="form-group">
                  <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block' }}>URL da Imagem (Thumbnail)</label>
                  <input
                    type="url"
                    value={formData.thumbnailUrl}
                    onChange={(e) => setFormData({ ...formData, thumbnailUrl: e.target.value })}
                    placeholder="https://..."
                    style={{ width: '100%', padding: '14px', fontSize: '16px', borderRadius: '8px', border: '2px solid var(--border-color)' }}
                  />
                </div>
              </div>

              {/* Instrutor e Configurações */}
              <div style={{ background: 'var(--info-bg)', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: 'var(--text-primary)', fontWeight: '600' }}>👤 Instrutor e Configurações</h4>
                
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block' }}>Nome do Instrutor</label>
                  <input
                    type="text"
                    value={formData.instructorName}
                    onChange={(e) => setFormData({ ...formData, instructorName: e.target.value })}
                    placeholder="Ex: João Silva"
                    style={{ width: '100%', padding: '14px', fontSize: '16px', borderRadius: '8px', border: '2px solid var(--border-color)' }}
                  />
                </div>

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block' }}>Créditos Necessários</label>
                    <input
                      type="number"
                      value={formData.creditsRequired}
                      onChange={(e) => setFormData({ ...formData, creditsRequired: e.target.value })}
                      min="0"
                      style={{ width: '100%', padding: '14px', fontSize: '16px', borderRadius: '8px', border: '2px solid var(--border-color)' }}
                    />
                  </div>
                  <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '32px' }}>
                    <input
                      type="checkbox"
                      id="isFreePreview"
                      checked={formData.isFreePreview}
                      onChange={(e) => setFormData({ ...formData, isFreePreview: e.target.checked })}
                      style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                    />
                    <label htmlFor="isFreePreview" style={{ marginBottom: 0, fontSize: '14px', cursor: 'pointer' }}>Pré-visualização gratuita</label>
                  </div>
                </div>
              </div>

              {/* Atribuir a Utilizadores */}
              <div style={{ background: 'var(--info-bg)', padding: '16px', borderRadius: '8px', marginBottom: '20px', border: '2px solid var(--info)' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: 'var(--info-text)', fontWeight: '600' }}>👥 Atribuir a Utilizadores</h4>
                
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: 'white', borderRadius: '8px', cursor: 'pointer', border: '2px solid var(--info)' }}>
                    <input
                      type="checkbox"
                      checked={assignToAll}
                      onChange={(e) => setAssignToAll(e.target.checked)}
                      style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '15px', fontWeight: '500', color: 'var(--info-text)' }}>✨ Atribuir a todos os utilizadores</span>
                  </label>
                </div>

                {!assignToAll && (
                  <div>
                    <div style={{ marginBottom: '12px' }}>
                      <input
                        type="text"
                        placeholder="🔍 Pesquisar utilizador..."
                        value={userSearchTerm}
                        onChange={(e) => setUserSearchTerm(e.target.value)}
                        style={{ width: '100%', padding: '12px', fontSize: '15px', borderRadius: '8px', border: '2px solid var(--info)' }}
                      />
                    </div>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <button
                        type="button"
                        onClick={toggleSelectAll}
                        style={{ padding: '8px 16px', fontSize: '13px', background: 'var(--info)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                      >
                        {selectedUsers.length === userList.length ? '✓ Desselecionar Todos' : '☐ Selecionar Todos'}
                      </button>
                      <span style={{ marginLeft: '12px', fontSize: '13px', color: 'var(--info)' }}>
                        {selectedUsers.length} de {userList.length} selecionados
                      </span>
                    </div>

                    <div style={{ maxHeight: '200px', overflowY: 'auto', background: 'white', borderRadius: '8px', border: '2px solid var(--info)' }}>
                      {filteredUsers.map(user => (
                        <label key={user.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', borderBottom: '1px solid var(--info)', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user.id)}
                            onChange={() => toggleUserSelection(user.id)}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--info-text)' }}>{user.name}</div>
                            <div style={{ fontSize: '12px', color: 'var(--info)' }}>{user.email}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid var(--info)', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowForm(false)} style={{ padding: '12px 24px', fontSize: '15px', borderRadius: '10px', border: '1px solid var(--info)', background: 'white', color: 'var(--info)', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s' }} onMouseOver={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }} onMouseOut={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#e2e8f0'; }}>Cancelar</button>
                <button type="submit" style={{ padding: '12px 32px', fontSize: '15px', borderRadius: '10px', border: 'none', background: 'var(--info)', color: 'white', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s' }} onMouseOver={(e) => e.currentTarget.style.background = '#2563eb'} onMouseOut={(e) => e.currentTarget.style.background = '#3b82f6'}>{editingClass ? 'Atualizar' : 'Criar Aula'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', padding: '16px 0', borderBottom: '1px solid var(--border-color, #e2e8f0)', marginBottom: '16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1', minWidth: '180px' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--info)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🔍 Pesquisar</label>
          <input type="text" placeholder="Pesquisar por título..." value={filterClassSearch} onChange={(e) => setFilterClassSearch(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--info)', fontSize: '14px' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--info)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📺 Tipo</label>
          <select value={filterClassType} onChange={(e) => setFilterClassType(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--info)', fontSize: '14px', minWidth: '130px' }}>
            <option value="">Todos</option>
            <option value="Live">🔴 Ao Vivo</option>
            <option value="Recorded">📹 Gravada</option>
            <option value="Hybrid">🔀 Híbrida</option>
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--info)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📊 Dificuldade</label>
          <select value={filterClassDifficulty} onChange={(e) => setFilterClassDifficulty(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--info)', fontSize: '14px', minWidth: '130px' }}>
            <option value="">Todas</option>
            <option value="Iniciante">🟢 Iniciante</option>
            <option value="Intermédio">🟡 Intermédio</option>
            <option value="Avançado">🔴 Avançado</option>
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--info)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📂 Categoria</label>
          <select value={filterClassCategory} onChange={(e) => setFilterClassCategory(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--info)', fontSize: '14px', minWidth: '130px' }}>
            <option value="">Todas</option>
            {[...new Set(classes.map(c => c.category).filter(Boolean))].map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        {(filterClassSearch || filterClassType || filterClassDifficulty || filterClassCategory) && (
          <button onClick={() => { setFilterClassSearch(''); setFilterClassType(''); setFilterClassDifficulty(''); setFilterClassCategory(''); }} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--info)', background: 'var(--info-bg)', color: 'var(--info)', fontSize: '13px', cursor: 'pointer', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px', height: '37px' }}>
            ✕ Limpar filtros
          </button>
        )}
      </div>

      {(() => {
        const filteredClasses = classes.filter((classItem) => {
          if (filterClassSearch && !classItem.title?.toLowerCase().includes(filterClassSearch.toLowerCase()) && !classItem.description?.toLowerCase().includes(filterClassSearch.toLowerCase())) return false;
          if (filterClassType && classItem.type !== filterClassType) return false;
          if (filterClassDifficulty && classItem.difficulty !== filterClassDifficulty) return false;
          if (filterClassCategory && classItem.category !== filterClassCategory) return false;
          return true;
        });
        return filteredClasses.length > 0 ? (
        <div className="services-grid">
          {filteredClasses.map((classItem) => (
            <div key={classItem.id} className="service-card" style={{ borderLeftColor: 'var(--accent-primary)' }}>
              {classItem.thumbnailUrl && (
                <img src={classItem.thumbnailUrl} alt={classItem.title} style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px', marginBottom: '12px' }} />
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <h4 style={{ margin: 0 }}>{classItem.title}</h4>
                {getTypeBadge(classItem.type)}
              </div>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>{classItem.description || 'Sem descrição'}</p>
              <div className="service-meta">
                <span>⏱️ {classItem.durationMinutes} min</span>
                <span>📊 {classItem.difficulty}</span>
                {classItem.category && <span>📂 {classItem.category}</span>}
              </div>
              {classItem.instructorName && (
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px' }}>
                  👤 {classItem.instructorName}
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(classItem)}>✏️ Editar</button>
                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(classItem.id)}>🗑️ Eliminar</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎬</div>
          <h4>{classes.length === 0 ? 'Sem aulas online' : 'Nenhuma aula corresponde aos filtros'}</h4>
          <p>{classes.length === 0 ? 'Crie a sua primeira aula para os utilizadores verem.' : 'Tente ajustar os filtros selecionados.'}</p>
        </div>
      );
      })()}
    </div>
  );
}

// ============================================
// ServiceTypesTab Component
// ============================================
function ServiceTypesTab() {
  const [typeList, setTypeList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState(null);
  // Filters
  const [filterTypeSearch, setFilterTypeSearch] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    durationMinutes: 60,
    defaultCapacity: 20,
    color: 'var(--accent-primary)',
  });

  useEffect(() => {
    loadTypes();
  }, []);

  const loadTypes = async () => {
    try {
      const response = await serviceTypes.getAll();
      setTypeList(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar tipos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    try {
      const dataToSend = {
        name: formData.name,
        description: formData.description,
        durationMinutes: parseInt(formData.durationMinutes) || 60,
        defaultCapacity: parseInt(formData.defaultCapacity) || 20,
        color: formData.color || '#6366f1',
      };
      if (editingType) {
        await serviceTypes.update(editingType.id, dataToSend);
        toast.success('Tipo atualizado!');
      } else {
        await serviceTypes.create(dataToSend);
        toast.success('Tipo criado!');
      }
      setShowForm(false);
      setEditingType(null);
      resetForm();
      loadTypes();
    } catch (error) {
      toast.error('Erro ao guardar tipo');
    }
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({
      name: type.name || '',
      description: type.description || '',
      durationMinutes: type.durationMinutes || 60,
      defaultCapacity: type.defaultCapacity || 20,
      color: type.color || '#6366f1',
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este tipo?')) return;
    try {
      await serviceTypes.delete(id);
      toast.success('Tipo eliminado');
      loadTypes();
    } catch (error) {
      toast.error('Erro ao eliminar tipo');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      durationMinutes: 60,
      defaultCapacity: 20,
      color: 'var(--accent-primary)',
    });
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner"></div></div>;
  }

  return (
    <div className="data-card">
      <div className="card-header">
        <h3 className="card-title">🏋️ Tipos de Aulas/Serviços</h3>
        <button className="btn btn-primary" onClick={() => { resetForm(); setEditingType(null); setShowForm(true); }}>+ Novo Tipo</button>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content clean-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '620px', width: '95%', maxHeight: '90vh', overflow: 'auto', background: 'white', borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '32px 40px 24px', borderBottom: '1px solid var(--info)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '26px', fontWeight: '600', color: 'var(--info-text)' }}>{editingType ? 'Editar Tipo de Aula' : 'Novo Tipo de Aula'}</h3>
                  <p style={{ margin: '8px 0 0 0', fontSize: '15px', color: 'var(--info)' }}>Configure o tipo de serviço oferecido no ginásio</p>
                </div>
                <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', color: 'var(--info)', fontSize: '28px', cursor: 'pointer', padding: '0', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', transition: 'all 0.2s' }} onMouseOver={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#0f172a'; }} onMouseOut={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#94a3b8'; }}>×</button>
              </div>
            </div>
            <form onSubmit={handleSubmit} style={{ padding: '32px 40px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '10px', display: 'block', color: 'var(--info-text)' }}>Nome do Tipo *</label>
                <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required placeholder="Ex: HIIT, Yoga, Pilates" style={{ width: '100%', padding: '13px 16px', fontSize: '15px', borderRadius: '10px', border: '1px solid var(--info)', outline: 'none', transition: 'border-color 0.2s' }} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '10px', display: 'block', color: 'var(--info-text)' }}>Descrição</label>
                <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} rows="3" placeholder="Descreva o tipo de aula..." style={{ width: '100%', padding: '13px 16px', fontSize: '15px', borderRadius: '10px', border: '1px solid var(--info)', outline: 'none', transition: 'border-color 0.2s', resize: 'vertical', fontFamily: 'inherit' }} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '10px', display: 'block', color: 'var(--info-text)' }}>Duração (min)</label>
                  <input type="number" value={formData.durationMinutes} onChange={e => setFormData({ ...formData, durationMinutes: e.target.value })} min="5" max="180" style={{ width: '100%', padding: '13px 16px', fontSize: '15px', borderRadius: '10px', border: '1px solid var(--info)', outline: 'none', transition: 'border-color 0.2s' }} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </div>
                <div>
                  <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '10px', display: 'block', color: 'var(--info-text)' }}>Capacidade</label>
                  <input type="number" value={formData.defaultCapacity} onChange={e => setFormData({ ...formData, defaultCapacity: e.target.value })} min="1" max="100" style={{ width: '100%', padding: '13px 16px', fontSize: '15px', borderRadius: '10px', border: '1px solid var(--info)', outline: 'none', transition: 'border-color 0.2s' }} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </div>
                <div>
                  <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '10px', display: 'block', color: 'var(--info-text)' }}>Cor</label>
                  <input type="color" value={formData.color} onChange={e => setFormData({ ...formData, color: e.target.value })} style={{ width: '100%', height: '48px', padding: '4px', borderRadius: '10px', border: '1px solid var(--info)', cursor: 'pointer' }} />
                </div>
              </div>
              <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid var(--info)', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowForm(false)} style={{ padding: '12px 24px', fontSize: '15px', borderRadius: '10px', border: '1px solid var(--info)', background: 'white', color: 'var(--info)', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s' }} onMouseOver={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }} onMouseOut={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#e2e8f0'; }}>Cancelar</button>
                <button type="submit" style={{ padding: '12px 32px', fontSize: '15px', borderRadius: '10px', border: 'none', background: 'var(--info)', color: 'white', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s' }} onMouseOver={(e) => e.currentTarget.style.background = '#2563eb'} onMouseOut={(e) => e.currentTarget.style.background = '#3b82f6'}>{editingType ? 'Atualizar' : 'Criar Tipo'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', padding: '16px 0', borderBottom: '1px solid var(--border-color, #e2e8f0)', marginBottom: '16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1', minWidth: '200px' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--info)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🔍 Pesquisar</label>
          <input type="text" placeholder="Pesquisar por nome ou descrição..." value={filterTypeSearch} onChange={(e) => setFilterTypeSearch(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--info)', fontSize: '14px' }} />
        </div>
        {filterTypeSearch && (
          <button onClick={() => setFilterTypeSearch('')} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--info)', background: 'var(--info-bg)', color: 'var(--info)', fontSize: '13px', cursor: 'pointer', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px', height: '37px' }}>
            ✕ Limpar filtros
          </button>
        )}
      </div>

      {(() => {
        const filteredTypes = typeList.filter((type) => {
          if (filterTypeSearch && !type.name?.toLowerCase().includes(filterTypeSearch.toLowerCase()) && !type.description?.toLowerCase().includes(filterTypeSearch.toLowerCase())) return false;
          return true;
        });
        return filteredTypes.length > 0 ? (
        <div className="services-grid">
          {filteredTypes.map((type) => (
            <div key={type.id} className="service-card" style={{ borderLeftColor: type.color }}>
              <h4>{type.name}</h4>
              <p>{type.description || 'Sem descrição'}</p>
              <div className="service-meta">
                <span>⏱️ {type.durationMinutes} min</span>
                <span>👥 {type.defaultCapacity} pessoas</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(type)}>✏️</button>
                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(type.id)}>🗑️</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
          {typeList.length === 0 ? 'Nenhum tipo de serviço criado' : 'Nenhum tipo corresponde à pesquisa'}
        </div>
      );
      })()}
    </div>
  );
}

// ============================================
// NotificationsTab Component - Admin Announcement Management
// ============================================
function NotificationsTab() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  // Filters
  const [filterNotifSearch, setFilterNotifSearch] = useState('');
  const [filterNotifType, setFilterNotifType] = useState('');
  const [filterNotifStatus, setFilterNotifStatus] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'info',
    isActive: true
  });

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      const res = await api.get('/notifications/admin');
      const data = res.data;
      setAnnouncements(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar anúncios:', error);
      setAnnouncements([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      // Preparar dados para enviar (sem isActive para criar, com Type capitalizado)
      const dataToSend = {
        title: formData.title,
        content: formData.content,
        type: formData.type.charAt(0).toUpperCase() + formData.type.slice(1) // Capitalize
      };
      
      if (editingAnnouncement) {
        await api.put(`/notifications/admin/${editingAnnouncement.id}`, dataToSend);
        toast.success('Anúncio atualizado com sucesso!');
      } else {
        await api.post('/notifications/admin', dataToSend);
        toast.success('Anúncio criado com sucesso!');
      }
      
      setShowForm(false);
      setEditingAnnouncement(null);
      setFormData({ title: '', content: '', type: 'info', isActive: true });
      loadAnnouncements();
    } catch (error) {
      toast.error('Erro ao guardar anúncio');
    }
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      type: announcement.type || 'info',
      isActive: announcement.isActive
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem a certeza que deseja desativar este anúncio?')) return;
    
    try {
      await api.delete(`/notifications/admin/${id}`);
      toast.success('Anúncio desativado');
      loadAnnouncements();
    } catch (error) {
      toast.error('Erro ao desativar anúncio');
    }
  };

  const handleActivate = async (id) => {
    try {
      await api.post(`/notifications/admin/${id}/activate`);
      toast.success('Anúncio ativado');
      loadAnnouncements();
    } catch (error) {
      toast.error('Erro ao ativar anúncio');
    }
  };

  const getTypeBadge = (type) => {
    const types = {
      info: { color: 'var(--info)', label: 'Informação' },
      warning: { color: 'var(--warning)', label: 'Aviso' },
      success: { color: 'var(--success)', label: 'Sucesso' },
      promo: { color: 'var(--accent-primary)', label: 'Promoção' }
    };
    const t = types[type] || types.info;
    return (
      <span style={{ 
        background: t.color + '20', 
        color: t.color, 
        padding: '4px 8px', 
        borderRadius: '4px', 
        fontSize: '12px',
        fontWeight: 500 
      }}>
        {t.label}
      </span>
    );
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner"></div></div>;
  }

  return (
    <div className="data-card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 className="card-title">🔔 Gestão de Anúncios</h3>
        <button 
          className="btn btn-primary"
          onClick={() => {
            setEditingAnnouncement(null);
            setFormData({ title: '', content: '', type: 'info', isActive: true });
            setShowForm(true);
          }}
        >
          + Novo Anúncio
        </button>
      </div>

      <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
        Crie anúncios para notificar os utilizadores da sua plataforma sobre novidades, avisos ou promoções.
      </p>

      {/* Filter Bar */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', padding: '0 0 16px 0', borderBottom: '1px solid var(--border-color, #e2e8f0)', marginBottom: '16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1', minWidth: '180px' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--info)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🔍 Pesquisar</label>
          <input type="text" placeholder="Pesquisar por título..." value={filterNotifSearch} onChange={(e) => setFilterNotifSearch(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--info)', fontSize: '14px' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--info)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🏷️ Tipo</label>
          <select value={filterNotifType} onChange={(e) => setFilterNotifType(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--info)', fontSize: '14px', minWidth: '140px' }}>
            <option value="">Todos</option>
            <option value="info">ℹ️ Informação</option>
            <option value="warning">⚠️ Aviso</option>
            <option value="success">✅ Sucesso</option>
            <option value="promo">🎁 Promoção</option>
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--info)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📊 Estado</label>
          <select value={filterNotifStatus} onChange={(e) => setFilterNotifStatus(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--info)', fontSize: '14px', minWidth: '130px' }}>
            <option value="">Todos</option>
            <option value="active">✅ Ativo</option>
            <option value="inactive">❌ Inativo</option>
          </select>
        </div>
        {(filterNotifSearch || filterNotifType || filterNotifStatus) && (
          <button onClick={() => { setFilterNotifSearch(''); setFilterNotifType(''); setFilterNotifStatus(''); }} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--info)', background: 'var(--info-bg)', color: 'var(--info)', fontSize: '13px', cursor: 'pointer', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px', height: '37px' }}>
            ✕ Limpar filtros
          </button>
        )}
      </div>

      {showForm && (
        <div style={{ 
          background: 'var(--bg-main)', 
          padding: '20px', 
          borderRadius: '12px', 
          marginBottom: '20px',
          border: '1px solid var(--border-color)'
        }}>
          <h4 style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>
            {editingAnnouncement ? '✏️ Editar Anúncio' : '➕ Novo Anúncio'}
          </h4>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Título *</label>
              <input
                type="text"
                className="form-input"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Ex: Novos horários disponíveis"
                required
              />
            </div>

            <div className="form-group">
              <label>Mensagem *</label>
              <textarea
                className="form-input"
                rows="3"
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                placeholder="Escreva a mensagem do anúncio..."
                required
                style={{ resize: 'vertical' }}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Tipo</label>
                <select
                  className="form-input"
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                >
                  <option value="info">ℹ️ Informação</option>
                  <option value="warning">⚠️ Aviso</option>
                  <option value="success">✅ Sucesso</option>
                  <option value="promo">🎁 Promoção</option>
                </select>
              </div>

              <div className="form-group">
                <label>Estado</label>
                <select
                  className="form-input"
                  value={formData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => setFormData({...formData, isActive: e.target.value === 'active'})}
                >
                  <option value="active">✅ Ativo</option>
                  <option value="inactive">❌ Inativo</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <button type="submit" className="btn btn-primary">
                {editingAnnouncement ? 'Atualizar' : 'Criar Anúncio'}
              </button>
              <button 
                type="button" 
                className="btn btn-outline"
                onClick={() => {
                  setShowForm(false);
                  setEditingAnnouncement(null);
                }}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      <table className="modern-table adm-table">
        <thead>
          <tr>
            <th>Título</th>
            <th>Tipo</th>
            <th>Estado</th>
            <th>Criado</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {(() => {
            const filteredAnnouncements = announcements.filter((ann) => {
              if (filterNotifSearch && !ann.title?.toLowerCase().includes(filterNotifSearch.toLowerCase()) && !ann.content?.toLowerCase().includes(filterNotifSearch.toLowerCase())) return false;
              if (filterNotifType && ann.type?.toLowerCase() !== filterNotifType) return false;
              if (filterNotifStatus === 'active' && !ann.isActive) return false;
              if (filterNotifStatus === 'inactive' && ann.isActive) return false;
              return true;
            });
            return filteredAnnouncements.length > 0 ? filteredAnnouncements.map((ann) => (
            <tr key={ann.id}>
              <td>
                <strong>{ann.title}</strong>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                  {ann.content?.substring(0, 60)}{ann.content?.length > 60 ? '...' : ''}
                </p>
              </td>
              <td data-label="Tipo">{getTypeBadge(ann.type)}</td>
              <td data-label="Estado">
                <span className={`status-badge ${ann.isActive ? 'confirmed' : 'cancelled'}`}>
                  {ann.isActive ? 'Ativo' : 'Inativo'}
                </span>
              </td>
              <td data-label="Criado">{ann.createdAt ? format(new Date(ann.createdAt), 'dd/MM/yyyy', { locale: pt }) : '-'}</td>
              <td className="adm-actions">
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    className="btn btn-sm btn-outline"
                    onClick={() => handleEdit(ann)}
                    title="Editar"
                  >
                    ✏️
                  </button>
                  {ann.isActive ? (
                    <button 
                      className="btn btn-sm btn-outline"
                      onClick={() => handleDelete(ann.id)}
                      title="Desativar"
                      style={{ color: 'var(--error)' }}
                    >
                      🚫
                    </button>
                  ) : (
                    <button 
                      className="btn btn-sm btn-outline"
                      onClick={() => handleActivate(ann.id)}
                      title="Ativar"
                      style={{ color: 'var(--success)' }}
                    >
                      ✅
                    </button>
                  )}
                </div>
              </td>
            </tr>
          )) : (
            <tr>
              <td colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                <p>{announcements.length === 0 ? 'Nenhum anúncio criado' : 'Nenhum anúncio corresponde aos filtros'}</p>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                  {announcements.length === 0 ? 'Clique em "Novo Anúncio" para criar o primeiro' : 'Tente ajustar os filtros selecionados'}
                </p>
              </td>
            </tr>
          );
          })()}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// SettingsTab Component
// ============================================
function BookingsTab() {
  const [bookingList, setBookingList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [allSlots, setAllSlots] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [bookingForm, setBookingForm] = useState({ userId: '', timeSlotId: '', notes: '' });
  // Filters
  const [filterBookingSearch, setFilterBookingSearch] = useState('');
  const [filterBookingDate, setFilterBookingDate] = useState('');
  const [filterBookingStatus, setFilterBookingStatus] = useState('');

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      const response = await bookings.getAll();
      setBookingList(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar reservas');
    } finally {
      setLoading(false);
    }
  };

  const loadOptions = async () => {
    try {
      const [usersRes, slotsRes] = await Promise.all([
        users.getAll().catch(() => ({ data: [] })),
        timeSlots.getAll().catch(() => ({ data: [] }))
      ]);
      setAllUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setAllSlots(Array.isArray(slotsRes.data) ? slotsRes.data : []);
    } catch {
      setAllUsers([]);
      setAllSlots([]);
    }
  };

  useEffect(() => {
    loadOptions();
  }, []);

  const openEditBooking = (booking) => {
    setEditingBooking(booking);
    setBookingForm({
      userId: booking.userId || '',
      timeSlotId: booking.timeSlotId || '',
      notes: booking.notes || ''
    });
    setShowEditModal(true);
  };

  const saveBookingEdit = async () => {
    if (!editingBooking) return;
    try {
      await bookings.adminUpdate(editingBooking.id, {
        userId: bookingForm.userId ? parseInt(bookingForm.userId, 10) : null,
        timeSlotId: bookingForm.timeSlotId ? parseInt(bookingForm.timeSlotId, 10) : null,
        notes: bookingForm.notes
      });
      toast.success('Reserva atualizada');
      setShowEditModal(false);
      setEditingBooking(null);
      await loadBookings();
    } catch (error) {
      toast.error(error.response?.data || 'Erro ao atualizar reserva');
    }
  };

  const approveBooking = async (bookingId) => {
    try {
      await bookings.approve(bookingId);
      toast.success('Reserva aprovada');
      await loadBookings();
    } catch {
      toast.error('Erro ao aprovar reserva');
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Eliminar esta reserva?')) return;
    try {
      await bookings.delete(bookingId);
      toast.success('Reserva eliminada');
      await loadBookings();
    } catch {
      toast.error('Erro ao eliminar reserva');
    }
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner"></div></div>;
  }

  return (
    <div className="data-card">
      <div className="card-header">
        <h3 className="card-title">Gestão de Reservas</h3>
      </div>

      {/* Filter Bar */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', padding: '16px 0', borderBottom: '1px solid var(--border-color, #e2e8f0)', marginBottom: '16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1', minWidth: '180px' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--info)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🔍 Pesquisar Utilizador</label>
          <input type="text" placeholder="Nome do utilizador..." value={filterBookingSearch} onChange={(e) => setFilterBookingSearch(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--info)', fontSize: '14px' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--info)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📅 Data</label>
          <input type="date" value={filterBookingDate} onChange={(e) => setFilterBookingDate(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--info)', fontSize: '14px' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--info)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📊 Estado</label>
          <select value={filterBookingStatus} onChange={(e) => setFilterBookingStatus(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--info)', fontSize: '14px', minWidth: '140px' }}>
            <option value="">Todos</option>
            <option value="pending">Pendente</option>
            <option value="completed">Completada</option>
            <option value="cancelled">Cancelada</option>
          </select>
        </div>
        {(filterBookingSearch || filterBookingDate || filterBookingStatus) && (
          <button onClick={() => { setFilterBookingSearch(''); setFilterBookingDate(''); setFilterBookingStatus(''); }} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--info)', background: 'var(--info-bg)', color: 'var(--info)', fontSize: '13px', cursor: 'pointer', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px', height: '37px' }}>
            ✕ Limpar filtros
          </button>
        )}
      </div>

      <table className="modern-table adm-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Utilizador</th>
            <th>Horário</th>
            <th>Data Reserva</th>
            <th>Estado</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {(() => {
            const filteredBookings = bookingList.filter((booking) => {
              if (filterBookingSearch && !booking.userName?.toLowerCase().includes(filterBookingSearch.toLowerCase())) return false;
              if (filterBookingDate && booking.slotStartTime) {
                const slotDate = new Date(booking.slotStartTime).toISOString().split('T')[0];
                if (slotDate !== filterBookingDate) return false;
              }
              if (filterBookingStatus === 'cancelled' && !booking.isCancelled) return false;
              if (filterBookingStatus === 'completed' && !booking.isCompleted) return false;
              if (filterBookingStatus === 'pending' && (booking.isCancelled || booking.isCompleted)) return false;
              return true;
            });
            return filteredBookings.length > 0 ? filteredBookings.map((booking) => (
            <tr key={booking.id}>
              <td data-label="ID">#{booking.id}</td>
              <td data-label="Utilizador">{booking.userName || 'N/A'}</td>
              <td data-label="Horário">{booking.slotStartTime ? format(new Date(booking.slotStartTime), 'dd/MM HH:mm') : '-'}</td>
              <td data-label="Data Reserva">{booking.createdAt ? format(new Date(booking.createdAt), 'dd/MM/yyyy HH:mm') : '-'}</td>
              <td data-label="Estado">
                <span className={`status-badge ${booking.isCancelled ? 'cancelled' : booking.isCompleted ? 'confirmed' : 'pending'}`}>
                  {booking.isCancelled ? 'Cancelada' : booking.isCompleted ? 'Completada' : 'Pendente'}
                </span>
              </td>
              <td className="adm-actions">
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button className="btn btn-sm btn-secondary" onClick={() => openEditBooking(booking)}>✏️ Editar</button>
                  {booking.isCancelled && (
                    <button className="btn btn-sm btn-primary" onClick={() => approveBooking(booking.id)}>✅ Aprovar</button>
                  )}
                  <button className="btn btn-sm btn-danger" onClick={() => deleteBooking(booking.id)}>🗑️ Eliminar</button>
                </div>
              </td>
            </tr>
          )) : (
            <tr>
              <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                {bookingList.length === 0 ? 'Nenhuma reserva encontrada' : 'Nenhuma reserva corresponde aos filtros selecionados'}
              </td>
            </tr>
          );
          })()}
        </tbody>
      </table>

      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" style={{ maxWidth: '560px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Editar Reserva #{editingBooking?.id}</h3>
            </div>
            <div className="form-grid" style={{ marginTop: '12px' }}>
              <div className="form-group">
                <label>Utilizador</label>
                <select
                  className="form-input"
                  value={bookingForm.userId}
                  onChange={(e) => setBookingForm({ ...bookingForm, userId: e.target.value })}
                >
                  <option value="">Selecionar...</option>
                  {allUsers.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Horário</label>
                <select
                  className="form-input"
                  value={bookingForm.timeSlotId}
                  onChange={(e) => setBookingForm({ ...bookingForm, timeSlotId: e.target.value })}
                >
                  <option value="">Selecionar...</option>
                  {allSlots.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.startTime ? format(new Date(s.startTime), 'dd/MM/yyyy HH:mm') : `#${s.id}`} {s.name ? `- ${s.name}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group" style={{ marginTop: '12px' }}>
              <label>Notas</label>
              <textarea
                className="form-input"
                rows={3}
                value={bookingForm.notes}
                onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
              />
            </div>
            <div className="form-actions" style={{ marginTop: '16px' }}>
              <button className="btn btn-outline" onClick={() => setShowEditModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={saveBookingEdit}>Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// SMS Validation Helper
// ============================================
/**
 * Validates if a user can receive SMS notifications
 * @param {Object} user - User object with receiveSmsNotifications flag
 * @param {boolean} showToast - Whether to show toast notification (default: true)
 * @returns {boolean} - True if user can receive SMS, false otherwise
 */
export function validateUserCanReceiveSms(user, showToast = true) {
  if (!user) {
    if (showToast) toast.error('❌ Utilizador não encontrado');
    return false;
  }
  
  if (!user.receiveSmsNotifications) {
    if (showToast) {
      toast.warning(
        `⚠️ ${user.name || 'Cliente'} tem as notificações SMS desativadas. Ative primeiro na ficha do cliente.`,
        { autoClose: 5000 }
      );
    }
    return false;
  }
  
  return true;
}

/**
 * Validates if a user can receive Email notifications
 * @param {Object} user - User object with receiveEmailNotifications flag
 * @param {boolean} showToast - Whether to show toast notification (default: true)
 * @returns {boolean} - True if user can receive Email, false otherwise
 */
export function validateUserCanReceiveEmail(user, showToast = true) {
  if (!user) {
    if (showToast) toast.error('❌ Utilizador não encontrado');
    return false;
  }
  
  if (!user.receiveEmailNotifications) {
    if (showToast) {
      toast.warning(
        `⚠️ ${user.name || 'Cliente'} tem as notificações por Email desativadas. Ative primeiro na ficha do cliente.`,
        { autoClose: 5000 }
      );
    }
    return false;
  }
  
  return true;
}

export default AdminDashboard;
